import React from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, FormHelperText, Stack } from '@mui/material';
import type { MuiTelInputCountry } from 'mui-tel-input';

import { UserIdentifier } from '@/api/generated';
import Field from '@/components/fields/Field';
import PhoneInput from '@/components/fields/PhoneInput';

interface InviteUserRowProps {
  /** Base name for the row (e.g., "assignments.0") - user field will be at `${name}.user`, phone at `${name}.phone` */
  name: string;
  /** User dropdown component (e.g. from getClientUserEmailDropdownSelect() or getMidotUserDropdownSelect()) */
  userDropdownSelect: React.ComponentType<Record<string, unknown>>;
  placeholder?: string;
  defaultCountry?: MuiTelInputCountry;
}

/**
 * InviteUserRow renders a single grid cell containing User dropdown.
 * When inviting by email, a Phone input appears next to it within the same cell.
 */
const InviteUserRow: React.FC<InviteUserRowProps> = ({ name, userDropdownSelect, placeholder, defaultCountry }) => {
  const { t } = useTranslation(['users', 'common']);
  const { control } = useFormContext();

  // Watch the user field value to determine if it's a new email
  const userValue = useWatch({
    control,
    name: `${name}.user`
  }) as UserIdentifier | undefined;

  // Get form errors for the user field
  const { errors } = useFormState({ control });
  const userError = get(errors, `${name}.user`);
  const userErrorMessage = userError?.message as string | undefined;

  // Show phone input when user has email (new invite) but no id (existing user)
  const showPhone = !!(userValue?.email && !userValue?.id);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        gap={1}
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          '& > *': {
            flex: showPhone ? '1 1 50%' : '1 1 100%',
            minWidth: 0,
            maxWidth: showPhone ? '50%' : '100%'
          },
          '& .MuiFormControl-root': {
            p: 0,
            m: 0,
            width: '100%'
          }
        }}
      >
        <Field
          component={userDropdownSelect}
          name={`${name}.user`}
          innerProps={{
            isMulti: false,
            usePortal: true,
            placeholder: placeholder || t('users:users.inviteClientUsers.selectUser'),
            sx: { width: '100%' }
          }}
          outerSx={{ p: 0, m: 0 }}
        />
        {showPhone && (
          <Field
            component={PhoneInput}
            name={`${name}.phone`}
            outerSx={{ p: 0, m: 0 }}
            innerProps={{ defaultCountry }}
          />
        )}
      </Stack>
      {userErrorMessage && (
        <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
          {userErrorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};

export default InviteUserRow;
